import type { InboundReceiveRoute } from "./inboundReceiveRoute";
import {
  inboundReceiveRouteRequiresPurchaseOrderLink,
  inboundReceiveRouteRequiresRmaLink,
  inboundReceiveRouteRequiresTransferLink,
} from "./inboundReceiveRoute";

export function logInboundArrivalSubmitBlockers(input: {
  vendorName: string;
  boxCountValid: boolean;
  noBolOrLabel: boolean;
  carrier: string;
  tracking: string;
  receiveRoute: InboundReceiveRoute | "";
  poNumbers: readonly string[];
  transferNumber: string;
  rmaNumber: string;
}): string[] {
  const blockers: string[] = [];
  if (input.vendorName.trim() === "") {
    blockers.push("Choose a vendor.");
  }
  if (!input.boxCountValid) {
    blockers.push("Enter a valid unit count (1 or more).");
  }
  if (!input.noBolOrLabel) {
    if (input.carrier.trim() === "") {
      blockers.push("Choose a carrier, or check No POD or label.");
    }
    if (input.tracking.trim() === "") {
      blockers.push(
        inboundArrivalIsHandDeliveredCarrier(input.carrier)
          ? "Enter the name of the person delivering, or check No POD or label."
          : "Enter tracking, or check No POD or label.",
      );
    }
  }
  if (input.receiveRoute !== "" && inboundReceiveRouteRequiresPurchaseOrderLink(input.receiveRoute)) {
    if (input.poNumbers.length === 0) {
      blockers.push("Link at least one purchase order for this route.");
    }
  } else if (input.receiveRoute !== "" && inboundReceiveRouteRequiresTransferLink(input.receiveRoute)) {
    if (input.transferNumber.trim() === "") {
      blockers.push("Link a transfer order for this route.");
    }
  } else if (input.receiveRoute !== "" && inboundReceiveRouteRequiresRmaLink(input.receiveRoute)) {
    if (input.rmaNumber.trim() === "") {
      blockers.push("Enter the RMA number for this route.");
    }
  }
  return blockers;
}

export function logInboundArrivalCanSubmit(input: {
  vendorName: string;
  boxCountValid: boolean;
  noBolOrLabel: boolean;
  carrier: string;
  tracking: string;
  receiveRoute: InboundReceiveRoute | "";
  poNumbers: readonly string[];
  transferNumber: string;
  rmaNumber: string;
  formBusy: boolean;
}): boolean {
  if (input.formBusy) return false;
  return logInboundArrivalSubmitBlockers(input).length === 0;
}

const ADD_CARRIER_OPTION_PREFIX = "__add_carrier__:";

export function inboundArrivalCarrierSelectOptions(
  carriers: readonly string[],
  searchValue: string,
): { value: string; label: string }[] {
  const base = carriers.map((carrier) => ({ value: carrier, label: carrier }));
  const search = searchValue.trim();
  if (search === "") return base;
  const hasExact = base.some((option) => option.label.toLowerCase() === search.toLowerCase());
  if (hasExact) return base;
  return [
    ...base,
    { value: `${ADD_CARRIER_OPTION_PREFIX}${search}`, label: `Add "${search}"` },
  ];
}

export function inboundArrivalIsHandDeliveredCarrier(carrier: string): boolean {
  return carrier.trim().toLowerCase().replace(/[\s_]+/g, "-") === "hand-delivered";
}

export function inboundArrivalTrackingPlaceholder(carrier: string): string {
  return inboundArrivalIsHandDeliveredCarrier(carrier) ? "Name of person delivering" : "1Z…";
}

export function inboundArrivalCarrierFromSelectValue(value: string): string {
  if (value.startsWith(ADD_CARRIER_OPTION_PREFIX)) {
    return value.slice(ADD_CARRIER_OPTION_PREFIX.length).trim();
  }
  return value.trim();
}

export function inboundArrivalNotesWithRmaLink(notes: string | null | undefined, rmaNumber: string): string | null {
  const trimmedRma = rmaNumber.trim();
  const trimmedNotes = (notes ?? "").trim();
  if (trimmedRma === "") {
    return trimmedNotes === "" ? null : trimmedNotes;
  }
  const rmaLine = `RMA ${trimmedRma}`;
  if (trimmedNotes === "") {
    return rmaLine;
  }
  return `${rmaLine}\n${trimmedNotes}`;
}
